import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, CheckCircle, ExternalLink, AlertCircle, Bot, ChevronLeft, ChevronRight, Lightbulb } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  triggerBugAnalysis,
  answerAndContinueAnalysis,
  createBugFixPR,
  type BugAIQuestion,
} from '@/services/data/bugAnalysis';

interface BugAIAnalysisPanelProps {
  bugReportId: string;
  onClose: () => void;
}

type Phase = 'analyzing' | 'questions' | 'ready' | 'creating_pr' | 'pr_submitted' | 'pr_created' | 'redirected_to_feature' | 'error';

export function BugAIAnalysisPanel({ bugReportId, onClose }: BugAIAnalysisPanelProps) {
  const [phase, setPhase] = useState<Phase>('analyzing');
  const [analysisId, setAnalysisId] = useState<string | null>(null);
  const [questions, setQuestions] = useState<BugAIQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [otherTexts, setOtherTexts] = useState<Record<string, string>>({});
  const [summary, setSummary] = useState('');
  const [fixPlan, setFixPlan] = useState('');
  const [prUrl, setPrUrl] = useState('');
  const [error, setError] = useState('');
  const [submittingQuestion, setSubmittingQuestion] = useState<string | null>(null);
  const startedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (startedForRef.current === bugReportId) return;
    startedForRef.current = bugReportId;
    setPhase('analyzing');
    setError('');
    const run = async () => {
      try {
        const result = await triggerBugAnalysis(bugReportId);
        const payload = typeof result === 'string' ? JSON.parse(result) : result;
        if (!payload?.action) {
          setError('Réponse invalide');
          setPhase('error');
          return;
        }
        setAnalysisId(payload.analysisId);
        handleAnalysisResult(payload);
      } catch (err: any) {
        setError(err.message || 'Erreur lors de l\'analyse');
        setPhase('error');
      }
    };
    run();
  }, [bugReportId]);

  const startAnalysis = async () => {
    setPhase('analyzing');
    setError('');
    try {
      const result = await triggerBugAnalysis(bugReportId);
      setAnalysisId(result.analysisId);
      handleAnalysisResult(result);
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'analyse');
      setPhase('error');
    }
  };

  const handleAnalysisResult = (result: any) => {
    if (result.action === 'redirected_to_feature') {
      setPhase('redirected_to_feature');
    } else if (result.action === 'questions') {
      setQuestions(result.questions || []);
      setCurrentQuestionIndex(0);
      setPhase('questions');
    } else if (result.action === 'ready') {
      setSummary(result.summary || '');
      setFixPlan(result.fixPlan || '');
      setPhase('ready');
    }
  };

  const handleAnswerQuestion = async (questionId: string) => {
    const selectedAnswer = answers[questionId];
    const finalAnswer = selectedAnswer === '__other__'
      ? (otherTexts[questionId] || '')
      : (selectedAnswer ?? otherTexts[questionId] ?? '');
    if (!finalAnswer.trim()) return;

    setSubmittingQuestion(questionId);
    try {
      const result = await answerAndContinueAnalysis(bugReportId, questionId, finalAnswer);
      setAnalysisId(result.analysisId);
      handleAnalysisResult(result);
    } catch (err: any) {
      setError(err.message || 'Erreur');
      setPhase('error');
    } finally {
      setSubmittingQuestion(null);
    }
  };

  const handleSkipQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handleValidateAndCreatePR = async () => {
    if (!analysisId) return;
    setPhase('creating_pr');
    try {
      const result = await createBugFixPR(analysisId);
      if (result.accepted) {
        setPhase('pr_submitted');
        return;
      }
      if (result.success && result.prUrl) {
        setPrUrl(result.prUrl);
        setPhase('pr_created');
      } else {
        setError(result.error || 'Erreur lors de la création de la PR');
        setPhase('error');
      }
    } catch (err: any) {
      setError(err.message || 'Erreur');
      setPhase('error');
    }
  };

  const currentQuestion = questions[currentQuestionIndex];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <Bot className="h-4 w-4" />
        Analyse IA
      </div>

      {/* Analyzing */}
      {phase === 'analyzing' && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <div>
            <p className="text-sm font-medium">Analyse en cours...</p>
            <p className="text-xs text-muted-foreground">Examen du bug et du code source</p>
          </div>
        </div>
      )}

      {/* Questions - Lovable style: one at a time */}
      {phase === 'questions' && currentQuestion && (
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          {/* Question header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="text-sm font-semibold">Questions</h3>
            <span className="text-xs text-muted-foreground">
              {questions.length > 1 ? `${currentQuestionIndex + 1} / ${questions.length}` : 'Sélectionne une réponse'}
            </span>
          </div>

          {/* Question body */}
          <div className="p-4 space-y-3">
            <p className="text-sm font-medium leading-snug">{currentQuestion.question_text}</p>

            {/* Options */}
            <div className="space-y-1.5">
              {((currentQuestion.options as string[]) || []).map((opt: string) => (
                <label
                  key={opt}
                  className={cn(
                    'flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-all text-sm',
                    answers[currentQuestion.id] === opt
                      ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                      : 'border-border hover:border-muted-foreground/30 hover:bg-muted/30'
                  )}
                >
                  <div className="mt-0.5 flex-shrink-0">
                    <div className={cn(
                      'h-4 w-4 rounded-full border-2 flex items-center justify-center transition-colors',
                      answers[currentQuestion.id] === opt
                        ? 'border-primary'
                        : 'border-muted-foreground/40'
                    )}>
                      {answers[currentQuestion.id] === opt && (
                        <div className="h-2 w-2 rounded-full bg-primary" />
                      )}
                    </div>
                  </div>
                  <span className="leading-snug">{opt}</span>
                </label>
              ))}

              {/* Other option */}
              <button
                type="button"
                className={cn(
                  'flex items-center gap-3 p-3 rounded-md border cursor-pointer transition-all text-sm text-left',
                  answers[currentQuestion.id] === '__other__'
                    ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                    : 'border-border hover:border-muted-foreground/30 hover:bg-muted/30'
                )}
                onClick={() => setAnswers(prev => ({ ...prev, [currentQuestion.id]: '__other__' }))}
              >
                <div className="flex-shrink-0">
                  <div className={cn(
                    'h-4 w-4 rounded-full border-2 flex items-center justify-center transition-colors',
                    answers[currentQuestion.id] === '__other__'
                      ? 'border-primary'
                      : 'border-muted-foreground/40'
                  )}>
                    {answers[currentQuestion.id] === '__other__' && (
                      <div className="h-2 w-2 rounded-full bg-primary" />
                    )}
                  </div>
                </div>
                {answers[currentQuestion.id] === '__other__' ? (
                  <Input
                    placeholder="Autre réponse..."
                    value={otherTexts[currentQuestion.id] || ''}
                    onChange={(e) => setOtherTexts(prev => ({ ...prev, [currentQuestion.id]: e.target.value }))}
                    onClick={(e) => e.stopPropagation()}
                    className="h-7 text-sm border-0 bg-transparent p-0 focus-visible:ring-0 shadow-none"
                    autoFocus
                  />
                ) : (
                  <span className="text-muted-foreground">Autre</span>
                )}
              </button>
            </div>

            {/* No options fallback: free text */}
            {(!currentQuestion.options || (currentQuestion.options as string[]).length === 0) && (
              <Input
                placeholder="Ta réponse..."
                value={otherTexts[currentQuestion.id] ?? ''}
                onChange={(e) => setOtherTexts(prev => ({ ...prev, [currentQuestion.id]: e.target.value }))}
              />
            )}
          </div>

          {/* Footer with navigation */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                disabled={currentQuestionIndex === 0}
                onClick={() => setCurrentQuestionIndex(prev => prev - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                disabled={currentQuestionIndex >= questions.length - 1}
                onClick={() => setCurrentQuestionIndex(prev => prev + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>

              {/* Progress dots */}
              {questions.length > 1 && (
                <div className="flex items-center gap-1 ml-2">
                  {questions.map((q) => {
                    const isCurrentQuestion = q === currentQuestion;
                    const isAnswered = answers[q.id];

                    const determineDotClass = () => {
                      if (isCurrentQuestion) {
                        return 'w-4 bg-primary';
                      }
                      if (isAnswered) {
                        return 'w-1.5 bg-primary/50';
                      }
                      return 'w-1.5 bg-muted-foreground/30';
                    };

                    const dotClass = determineDotClass();

                    return (
                      <div
                        key={q.id}
                        className={cn('h-1.5 rounded-full transition-all', dotClass)}
                      />
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              {questions.length > 1 && currentQuestionIndex < questions.length - 1 && (
                <Button variant="ghost" size="sm" onClick={handleSkipQuestion} className="text-muted-foreground">
                  Passer
                </Button>
              )}
              <Button
                size="sm"
                onClick={() => handleAnswerQuestion(currentQuestion.id)}
                disabled={
                  submittingQuestion === currentQuestion.id ||
                  !(
                    (answers[currentQuestion.id] && answers[currentQuestion.id] !== '__other__') ||
                    (answers[currentQuestion.id] === '__other__' && otherTexts[currentQuestion.id]?.trim()) ||
                    ((!currentQuestion.options || (currentQuestion.options as string[]).length === 0) && otherTexts[currentQuestion.id]?.trim())
                  )
                }
              >
                {submittingQuestion === currentQuestion.id ? (
                  <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Envoi...</>
                ) : 'Répondre'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Ready - Show plan and validate */}
      {phase === 'ready' && (
        <div className="space-y-3">
          <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-sm font-medium">Analyse terminée</span>
            </div>
            {summary && <p className="text-sm text-muted-foreground mb-2">{summary}</p>}
            {fixPlan && (
              <div className="text-xs bg-muted/50 rounded p-2 max-h-40 overflow-y-auto whitespace-pre-wrap">
                {fixPlan}
              </div>
            )}
          </div>
          <Button onClick={handleValidateAndCreatePR} className="w-full">
            ✅ Valider et créer la PR
          </Button>
        </div>
      )}

      {/* Creating PR */}
      {phase === 'creating_pr' && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <p className="text-sm font-medium">Envoi en cours...</p>
        </div>
      )}

      {/* PR submitted */}
      {phase === 'pr_submitted' && (
        <div className="space-y-3">
          <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-sm font-medium">Rapport pris en compte</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Tu seras notifié par message quand la correction sera en ligne
            </p>
          </div>
          <Button variant="outline" onClick={onClose} className="w-full">Fermer</Button>
        </div>
      )}

      {/* PR Created */}
      {phase === 'pr_created' && (
        <div className="space-y-3">
          <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-sm font-medium">PR créée avec succès !</span>
            </div>
          </div>
          {prUrl && (
            <a href={prUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-primary hover:underline">
              <ExternalLink className="h-4 w-4" /> Voir la Pull Request sur GitHub
            </a>
          )}
          <Button variant="outline" onClick={onClose} className="w-full">Fermer</Button>
        </div>
      )}

      {/* Redirected to feature */}
      {phase === 'redirected_to_feature' && (
        <div className="space-y-3">
          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb className="h-4 w-4 text-amber-500" />
              <span className="text-sm font-medium">Redirigé vers le Product Board</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Ce signalement implique une modification structurelle de l'interface. Il a été automatiquement transféré comme demande de fonctionnalité.
            </p>
          </div>
          <Button variant="outline" onClick={onClose} className="w-full">Fermer</Button>
        </div>
      )}

      {/* Error */}
      {phase === 'error' && (
        <div className="space-y-3">
          <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-destructive" />
              <span className="text-sm font-medium">Erreur</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">{error}</p>
          </div>
          <Button variant="outline" size="sm" onClick={startAnalysis}>Réessayer</Button>
        </div>
      )}
    </div>
  );
}
